import React from 'react';
import { ReviewCorrectionScope } from '../../../api/structs';
import {
  reviewScopeOptionLabel,
  reviewScopeOptionsFor,
} from '../../pipeline/reviewer/lessonScope';

/**
 * What the judge is asked to keep a lesson under, and therefore what the
 * operator's edit is held to: the whole lessons block is capped at 1200 chars
 * before the judge ever sees it, so a rule that runs long crowds out the others.
 */
export const LESSON_MAX_CHARS = 280;

/**
 * Whether a lesson is too long to save. Checked rather than ENFORCED: the text
 * can arrive over the cap (a judge that ignored its instruction, an operator
 * pasting a paragraph), and silently truncating someone's rule mid-sentence
 * would ship a different rule than the one they read.
 */
export const lessonTooLong = (lesson: string): boolean => lesson.length > LESSON_MAX_CHARS;

interface VerdictLessonEditorProps {
  /** The note the lesson was distilled from has changed since. */
  stale?: boolean;
  lesson: string;
  onLessonChange: (value: string) => void;
  scope: ReviewCorrectionScope;
  onScopeChange: (value: ReviewCorrectionScope) => void;
  /**
   * `<provider>/<model>` of the generator that made what was judged; '' when
   * the checkpoint names none. It is what the model scope would bind the rule
   * to, so without it that scope is not offered.
   */
  modelId: string;
  /** The lesson this one repeats; '' when it is a new one. */
  reinforcesId: string;
  /** That lesson's text — '' while it is being read, or if it cannot be. */
  reinforcedText: string;
  /** Ticked = record a SEPARATE lesson instead of reinforcing the existing one. */
  separate: boolean;
  onSeparateChange: (value: boolean) => void;
  /** The one repair the distillation proposed; '' for none. */
  suggestedAction: string;
  /** Whether this surface can actually perform that repair. */
  canDropClosingFrame: boolean;
  dropClosingFrame: boolean;
  onDropClosingFrameChange: (value: boolean) => void;
  disabled?: boolean;
}

/**
 * The distilled lesson, before it is saved: the rule itself (editable — the
 * judge wrote it, the operator owns it), how far it reaches, and whether it
 * repeats one that already exists.
 *
 * Nothing here is decided for the operator behind their back. The rule is shown
 * in full and can be rewritten to nothing; the scope arrives prefilled but is a
 * plain select; reinforcing an existing lesson is the default only because a
 * duplicate rule is worse than a counted one, and one checkbox undoes it.
 */
const VerdictLessonEditor: React.FC<VerdictLessonEditorProps> = ({
  stale,
  lesson,
  onLessonChange,
  scope,
  onScopeChange,
  modelId,
  reinforcesId,
  reinforcedText,
  separate,
  onSeparateChange,
  suggestedAction,
  canDropClosingFrame,
  dropClosingFrame,
  onDropClosingFrameChange,
  disabled,
}) => (
  <div className="space-y-1.5 border-t border-white/10 pt-1.5" data-testid="verdict-lesson-editor">
    <div className="flex items-baseline justify-between gap-1.5">
      <p className="text-[10px] uppercase tracking-wide text-zinc-500">The lesson</p>
      <span
        className={`font-mono text-[10px] ${
          lessonTooLong(lesson) ? 'text-amber-300' : 'text-zinc-600'
        }`}
        data-testid="verdict-lesson-count"
        data-state={lessonTooLong(lesson) ? 'over' : 'ok'}
      >
        {`${lesson.length}/${LESSON_MAX_CHARS}`}
      </span>
    </div>

    {stale && (
      <p className="text-[10px] leading-relaxed text-amber-300" data-testid="verdict-lesson-stale">
        The note changed — distil again or save the lesson as edited.
      </p>
    )}

    <textarea
      aria-label="Distilled lesson"
      rows={3}
      disabled={disabled}
      value={lesson}
      onChange={(event) => onLessonChange(event.target.value)}
      className="w-full resize-y border border-white/10 bg-black/60 px-1.5 py-1 font-mono text-[11px] leading-relaxed text-zinc-200 placeholder:text-zinc-600"
    />

    <label className="flex items-center gap-1.5 text-[11px] text-zinc-300">
      <span className="text-[10px] uppercase tracking-wide text-zinc-500">Applies to</span>
      <select
        aria-label="Lesson scope"
        disabled={disabled}
        value={scope}
        onChange={(event) => onScopeChange(event.target.value as ReviewCorrectionScope)}
        className="border border-white/10 bg-black/60 px-1.5 py-1 text-[11px] text-zinc-200"
      >
        {reviewScopeOptionsFor(modelId, scope).map((option) => (
          <option key={option} value={option}>
            {reviewScopeOptionLabel(option, modelId)}
          </option>
        ))}
      </select>
    </label>

    {reinforcesId && (
      <div className="space-y-0.5" data-testid="verdict-lesson-reinforces">
        <p className="text-[10px] leading-relaxed text-indigo-300">
          {`Reinforces an existing lesson${reinforcedText ? `: ${reinforcedText}` : ''}`}
        </p>
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-400">
          <input
            type="checkbox"
            disabled={disabled}
            checked={separate}
            onChange={(event) => onSeparateChange(event.target.checked)}
          />
          <span>Save as a new lesson instead</span>
        </label>
      </div>
    )}

    {suggestedAction === 'drop_closing_frame' && canDropClosingFrame && (
      <label
        className="flex items-center gap-1.5 text-[11px] text-amber-200"
        data-testid="verdict-lesson-drop-closing"
      >
        <input
          type="checkbox"
          disabled={disabled}
          checked={dropClosingFrame}
          onChange={(event) => onDropClosingFrameChange(event.target.checked)}
        />
        <span>Also drop the closing frame of this scene now</span>
      </label>
    )}
  </div>
);

export default VerdictLessonEditor;
